import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Mail, Lock, AlertTriangle, CheckCircle, User, ArrowRight, RefreshCw, X } from 'lucide-react';

// Loading Transition Component
const LoadingTransition = ({ isLoading }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (isLoading) {
      setProgress(0);
      const timer = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) {
            clearInterval(timer);
            return 90;
          }
          return prev + 10;
        });
      }, 150);
      return () => clearInterval(timer);
    } else {
      setProgress(100);
    }
  }, [isLoading]);

  if (!isLoading && progress === 0) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center bg-white transition-opacity duration-500 ${
        !isLoading && progress === 100 ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
    >
      <div className="text-center">
        {/* Animated GIF */}
        <div className="mb-2 flex justify-center">
          <img 
            src="/vdart-loading.gif"
            alt="Loading"
            className="w-48 h-48 object-contain"
            onError={(e) => {
              console.error('Failed to load vdart-loading.gif');
              e.target.style.display = 'none';
            }}
          />
        </div>

        {/* Loading Text */}
        <div className="text-gray-800 space-y-4">
          <h2 className="text-3xl font-bold animate-pulse">
            Signing you in...
          </h2>
          <p className="text-gray-600 text-lg">
            Preparing your dashboard
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mt-8 w-80 mx-auto">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-600 to-cyan-500 rounded-full transition-all duration-300 ease-out shadow-lg"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-gray-600 text-sm mt-2">{progress}%</p>
        </div>

        {/* Animated Dots */}
        <div className="flex justify-center gap-2 mt-6">
          <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-3 h-3 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );
};

export default function CandidatePortalAuth({ onLoginSuccess }) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoginTransition, setIsLoginTransition] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [signupData, setSignupData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    role: 'Account Manager'
  });

  const [otpData, setOtpData] = useState({
    email: '',
    otp: '',
    isOtpSent: false,
    isVerifying: false
  });

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
const [isForgotPassword, setIsForgotPassword] = useState(false);
const [forgotStep, setForgotStep] = useState(1); // 1=email, 2=otp, 3=new password
const [forgotData, setForgotData] = useState({
  email: '',
  otp: '',
  newPassword: '',
  confirmPassword: ''
});
const [showNewPassword, setShowNewPassword] = useState(false);
const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const slides = [
    {
      iframeSrc: "https://lottie.host/embed/f35999a1-9712-48fe-8b79-addbeccfe9be/omYltoUyTh.lottie",
      title: "Candidate Database",
      description: "Centralized repository for all candidate information with advanced search and filtering capabilities"
    },
    {
      iframeSrc: "https://lottie.host/embed/4d1078f1-f194-450c-acfd-d9591691e879/rBP8FMyf9z.lottie",
      title: "Analytics Dashboard",
      description: "Real-time insights and metrics to track recruitment performance and optimize hiring strategies"
    },
    {
      iframeSrc: "https://lottie.host/embed/27a22ebf-5cfd-43fb-bc2d-bee0318dd07a/B24F9rzHmo.lottie",
      title: "Email Reminders",
      description: "Automated email notifications and reminders to keep your recruitment process on track"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  // Login Handler (NO TOAST)
  const handleLogin = async () => {
    const errors = {};
    if (!loginData.email) {
      errors.email = 'Email is required';
    } else {
      const allowedDomains = ['@vdartinc.com', '@vdartdigital.com', '@trustpeople.com'];
      const isValidDomain = allowedDomains.some(domain => loginData.email.toLowerCase().endsWith(domain));
      if (!isValidDomain) {
        errors.email = 'Please use a company email address';
      }
    }
    if (!loginData.password) errors.password = 'Password is required';
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
    setFormErrors({});

    try {
      const response = await fetch('https://test.vdpl.co/Candidates/Candidates/login.php', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(loginData)
      });
      
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        setIsLoading(false);
        return;
      }
      
      if (response.ok && data.success) {
  localStorage.setItem('user', JSON.stringify(data.user));
  localStorage.setItem('session_token', data.session_token);
  localStorage.setItem('isLoggedIn', 'true');
  
  // Show loading transition
  setIsLoginTransition(true);
  
  // Wait for animation to complete before redirecting
  setTimeout(() => {
    onLoginSuccess(data.user);
  }, 3000);
} else {
  setToast({ show: true, message: data.message || 'Login failed. Please try again.', type: 'error' });
}
    } catch (error) {
  console.error('Login error:', error);
  setToast({ show: true, message: 'Connection error. Please try again.', type: 'error' });
} finally {
      setIsLoading(false);
    }
  };

  // Signup Handler (WITH TOAST)
  const handleSignup = async () => {
  if (otpData.isOtpSent) {
    await handleOtpVerification();
    return;
  }

  const errors = {};
  if (!signupData.first_name) errors.first_name = 'First name is required';
  if (!signupData.last_name) errors.last_name = 'Last name is required';
  if (!signupData.email) {
    errors.email = 'Email is required';
  } else {
    const allowedDomains = ['@vdartinc.com', '@vdartdigital.com', '@trustpeople.com'];
    const isValidDomain = allowedDomains.some(domain => signupData.email.toLowerCase().endsWith(domain));
    if (!isValidDomain) {
      errors.email = 'Please use a company email address';
    }
  }
  if (!signupData.password) {
  errors.password = 'Password is required';
} else {
  const pwd = signupData.password;
  if (pwd.length < 12) {
    errors.password = 'Password must be at least 12 characters';
  } else if (!/[A-Z]/.test(pwd)) {
    errors.password = 'Must contain at least one uppercase letter';
  } else if (!/[a-z]/.test(pwd)) {
    errors.password = 'Must contain at least one lowercase letter';
  } else if (!/[0-9]/.test(pwd)) {
    errors.password = 'Must contain at least one number';
  } else if (!/[!@#$%^&*()\-_=+\[\]{};:'",.<>/?\\|`~]/.test(pwd)) {
    errors.password = 'Must contain at least one special character (!@#$%^&*...)';
  }
}
  if (!signupData.confirm_password) errors.confirm_password = 'Please confirm your password';
  if (signupData.password !== signupData.confirm_password) {
    errors.confirm_password = 'Passwords do not match';
  }
  
  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }

  setIsLoading(true);
  setFormErrors({});

  try {
    console.log('🔄 Sending signup request...');
    
    const response = await fetch('https://test.vdpl.co/Candidates/Candidates/signup.php', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(signupData)
    });
    
    const responseText = await response.text();
    console.log('📥 Raw Response:', responseText);
    
    let data;
    
    try {
      data = JSON.parse(responseText);
      console.log('📦 Parsed Data:', data);
    } catch (parseError) {
      console.error('❌ JSON Parse Error:', parseError);
      console.error('📄 Response Text:', responseText);
      setToast({ show: true, message: 'Server error. Please check console for details.', type: 'error' });
      setIsLoading(false);
      return;
    }
    
    if (response.ok && data.success && data.requires_otp) {
      console.log('✅ OTP Sent Successfully!');
      console.log('📧 Email:', data.email);
      if (data.debug) {
        console.log('🐛 Debug Info:', data.debug);
      }
      
      setOtpData({
        email: data.email,
        otp: '',
        isOtpSent: true,
        isVerifying: false
      });
      setToast({ show: true, message: 'Verification code sent to your email!', type: 'success' });
    } else {
      console.error('❌ Signup Failed:', data.message);
      if (data.debug) {
        console.error('🐛 Debug Info:', data.debug);
      }
      setToast({ show: true, message: data.message || 'Signup failed', type: 'error' });
    }
  } catch (error) {
    console.error('❌ Network Error:', error);
    setToast({ show: true, message: 'Connection error. Please try again.', type: 'error' });
  } finally {
    setIsLoading(false);
  }
};

  // OTP Verification Handler (WITH TOAST)
  const handleOtpVerification = async () => {
    if (!otpData.otp || otpData.otp.length !== 6) {
      setFormErrors({ otp: 'Please enter the 6-digit verification code' });
      return;
    }

    setIsLoading(true);
    setFormErrors({});
    setOtpData(prev => ({ ...prev, isVerifying: true }));

    try {
      const response = await fetch('https://test.vdpl.co/Candidates/Candidates/signup.php', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          action: 'verify_otp',
          email: otpData.email,
          otp: otpData.otp
        })
      });
      
      const responseText = await response.text();
      let data = JSON.parse(responseText);
      
      if (response.ok && data.success) {
        setToast({ show: true, message: 'Account created successfully! Please login.', type: 'success' });
        setTimeout(() => {
          setIsSignUp(false);
          setOtpData({ email: '', otp: '', isOtpSent: false, isVerifying: false });
          setSignupData({
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            confirm_password: '',
            role: 'Account Manager'
          });
        }, 2000);
      } else {
        setToast({ show: true, message: data.message || 'Invalid verification code', type: 'error' });
        setOtpData(prev => ({ ...prev, isVerifying: false }));
      }
    } catch (error) {
      setToast({ show: true, message: 'Verification failed. Please try again.', type: 'error' });
      setOtpData(prev => ({ ...prev, isVerifying: false }));
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP (WITH TOAST)
  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('https://test.vdpl.co/Candidates/Candidates/signup.php', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(signupData)
      });
      
      const data = await response.json();
      if (data.success) {
        setToast({ show: true, message: 'New code sent to your email!', type: 'success' });
      }
    } catch (error) {
      setToast({ show: true, message: 'Failed to resend code', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
  const errors = {};

  if (forgotStep === 1) {
    if (!forgotData.email) {
      errors.email = 'Email is required';
    } else {
      const allowedDomains = ['@vdartinc.com', '@vdartdigital.com', '@trustpeople.com'];
      const isValidDomain = allowedDomains.some(domain => forgotData.email.toLowerCase().endsWith(domain));
      if (!isValidDomain) errors.email = 'Please use a company email address';
    }
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setIsLoading(true);
    setFormErrors({});
    try {
      const response = await fetch('https://test.vdpl.co/Candidates/Candidates/forgot_password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotData.email })
      });
      const data = await response.json();
      if (data.success) {
        setForgotStep(2);
        setToast({ show: true, message: 'Verification code sent to your email!', type: 'success' });
      } else {
        setToast({ show: true, message: data.message || 'Failed to send code', type: 'error' });
      }
    } catch {
      setToast({ show: true, message: 'Connection error. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }

  } else if (forgotStep === 2) {
    if (!forgotData.otp || forgotData.otp.length !== 6) {
      errors.otp = 'Please enter the 6-digit verification code';
      setFormErrors(errors); return;
    }
    setIsLoading(true);
    setFormErrors({});
    try {
      const response = await fetch('https://test.vdpl.co/Candidates/Candidates/forgot_password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotData.email, otp: forgotData.otp })
      });
      const data = await response.json();
      if (data.success) {
        setForgotStep(3);
        setToast({ show: true, message: 'Code verified! Set your new password.', type: 'success' });
      } else {
        setToast({ show: true, message: data.message || 'Invalid verification code', type: 'error' });
      }
    } catch {
      setToast({ show: true, message: 'Connection error. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }

  } else if (forgotStep === 3) {
    if (!forgotData.newPassword) {
  errors.newPassword = 'Password is required';
} else {
  const pwd = forgotData.newPassword;
  if (pwd.length < 12) {
    errors.newPassword = 'Password must be at least 12 characters';
  } else if (!/[A-Z]/.test(pwd)) {
    errors.newPassword = 'Must contain at least one uppercase letter';
  } else if (!/[a-z]/.test(pwd)) {
    errors.newPassword = 'Must contain at least one lowercase letter';
  } else if (!/[0-9]/.test(pwd)) {
    errors.newPassword = 'Must contain at least one number';
  } else if (!/[!@#$%^&*()\-_=+\[\]{};:'",.<>/?\\|`~]/.test(pwd)) {
    errors.newPassword = 'Must contain at least one special character (!@#$%^&*...)';
  }
}
    if (!forgotData.confirmPassword) errors.confirmPassword = 'Please confirm your password';
    else if (forgotData.newPassword !== forgotData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    setIsLoading(true);
    setFormErrors({});
    try {
      const response = await fetch('https://test.vdpl.co/Candidates/Candidates/forgot_password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotData.email,
          otp: forgotData.otp,
          new_password: forgotData.newPassword,
          confirm_password: forgotData.confirmPassword
        })
      });
      const data = await response.json();
      if (data.success) {
        setToast({ show: true, message: 'Password reset successfully! Please sign in.', type: 'success' });
        setTimeout(() => {
          setIsForgotPassword(false);
          setForgotStep(1);
          setForgotData({ email: '', otp: '', newPassword: '', confirmPassword: '' });
        }, 2000);
      } else {
        setToast({ show: true, message: data.message || 'Failed to reset password', type: 'error' });
      }
    } catch {
      setToast({ show: true, message: 'Connection error. Please try again.', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }
};

  const handleLoginKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const handleSignupKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSignup();
    }
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: 'success' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes scaleIn {
          0% {
            transform: scale(0);
          }
          50% {
            transform: scale(1.2);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes progressBar {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }

        .animate-slideIn {
          animation: slideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .animate-scaleIn {
          animation: scaleIn 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .animate-progressBar {
          animation: progressBar 5s linear;
        }
      `}</style>

      {/* Loading Transition for Login */}
      <LoadingTransition isLoading={isLoginTransition} />

      {/* Enhanced Toast Notification */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-50 animate-slideIn">
          <div className={`${
            toast.type === 'success' 
              ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
              : 'bg-gradient-to-r from-red-500 to-rose-600'
          } text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center space-x-4 min-w-[320px] backdrop-blur-sm border border-white/20`}>
            
            {/* Icon with animation */}
            <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
              toast.type === 'success' ? 'bg-white/20' : 'bg-white/20'
            }`}>
              {toast.type === 'success' ? (
                <CheckCircle className="w-6 h-6 animate-scaleIn" />
              ) : (
                <AlertTriangle className="w-6 h-6 animate-bounce" />
              )}
            </div>
            
            {/* Message */}
            <div className="flex-1">
              <p className="font-semibold text-sm mb-0.5">
                {toast.type === 'success' ? 'Success!' : 'Error'}
              </p>
              <p className="text-sm opacity-95">{toast.message}</p>
            </div>
            
            {/* Close button */}
            <button 
              onClick={() => setToast({ show: false, message: '', type: 'success' })}
              className="flex-shrink-0 w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-all duration-200 group"
            >
              <X className="w-5 h-5 group-hover:scale-110 transition-transform" />
            </button>
            
            {/* Progress bar */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 rounded-b-2xl overflow-hidden">
              <div className="h-full bg-white animate-progressBar origin-left"></div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row w-full">
        
        {/* Left Side - Auto-Sliding Content */}
        <div className="w-full md:w-1/2 bg-gray-50 p-8 md:p-12 lg:p-16 flex flex-col justify-center relative overflow-hidden min-h-screen order-2 md:order-1">
          
          <div className="absolute inset-0 opacity-5">
            <div className="absolute bottom-20 right-10 w-24 h-24 bg-gray-800 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-gray-800 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
          </div>

          <div className="relative z-10 w-full max-w-2xl mx-auto">
            <div className="mb-12 text-center md:text-left">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl text-gray-800 mb-2">
                Candidate Management Portal <span className="text-blue-600">(CMP)</span>
              </h1>
              <p className="text-gray-600 text-base sm:text-lg lg:text-xl">
                Streamline your recruitment process
              </p>
            </div>

            <div className="relative h-[300px] sm:h-[350px] lg:h-[380px]">
              {slides.map((slide, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                    index === currentSlide
                      ? 'opacity-100 translate-x-0'
                      : index < currentSlide
                      ? 'opacity-0 -translate-x-full'
                      : 'opacity-0 translate-x-full'
                  }`}
                >
                  <div className="mb-10 flex justify-center">
                    <iframe
                      src={slide.iframeSrc}
                      style={{ width: '240px', height: '240px', border: 'none' }}
                      title={`${slide.title} animation`}
                      className="sm:w-[260px] sm:h-[260px] lg:w-[280px] lg:h-[280px]"
                    />
                  </div>

                  <h2 className="text-xl sm:text-2xl lg:text-3xl text-gray-800 mb-3 text-center">
                    {slide.title}
                  </h2>

                  <p className="text-gray-600 text-center px-8 text-sm sm:text-base lg:text-lg leading-relaxed">
                    {slide.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex justify-center gap-3 mt-10">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? 'w-10 bg-blue-600' 
                      : 'w-2 bg-gray-400 hover:bg-gray-600'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Forms */}
        <div className="w-full md:w-1/2 p-8 md:p-12 lg:p-16 flex items-center justify-center bg-white order-1 md:order-2 relative min-h-screen">
          
          {/* Organization Logo - Top Right */}
          <div className="absolute top-4 right-4 md:top-6 md:right-6">
            <img
              src="VDart_Logo.png"
              alt="VDart Logo"
              className="h-8 sm:h-9 lg:h-10 w-auto object-contain"
            />
          </div>

          <div className="w-full max-w-md">
            
            {/* Sign In Form */}
            <div className={`transition-all duration-500 ${isSignUp || isForgotPassword ? 'hidden' : 'block'}`}>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-gray-800 mb-2">Sign In</h2>
              <p className="text-gray-600 mb-6 lg:mb-7 text-sm sm:text-base">Access your candidate management portal</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm sm:text-base text-gray-700 mb-2">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    <input
                      type="email"
                      placeholder="your.email@vdartinc.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      onKeyPress={handleLoginKeyPress}
                      className={`w-full pl-10 sm:pl-11 pr-4 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                        formErrors.email ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {formErrors.email && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {formErrors.email}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm sm:text-base text-gray-700 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      onKeyPress={handleLoginKeyPress}
                      className={`w-full pl-10 sm:pl-11 pr-12 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                        formErrors.password ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
                    </button>
                  </div>
                  {formErrors.password && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      {formErrors.password}
                    </p>
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    {/* <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded" />
                    <span className="ml-2 text-xs sm:text-sm text-gray-600">Remember me</span> */}
                  </label>
                  <button
  type="button"
  onClick={() => { setIsForgotPassword(true); setFormErrors({}); }}
  className="text-xs sm:text-sm text-blue-600 hover:underline"
>
  Forgot password?
</button>
                </div>
                
                <button
                  onClick={handleLogin}
                  disabled={isLoading}
                  className={`w-full py-3 sm:py-3.5 rounded-lg transition-all duration-300 text-sm sm:text-base ${
                    isLoading 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white hover:shadow-lg transform hover:scale-105'
                  }`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Signing In...
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </div>
              
              <div className="mt-6 text-center">
                <p className="text-gray-600 text-sm sm:text-base">
                  Don't have an account?{' '}
                  <button
                    onClick={() => {
                      setIsSignUp(true);
                      setFormErrors({});
                    }}
                    className="text-blue-600 hover:underline"
                  >
                    Sign Up
                  </button>
                </p>
              </div>
            </div>


{/* Forgot Password Form */}
<div className={`transition-all duration-500 ${isForgotPassword ? 'block' : 'hidden'}`}>
  
  {/* Step Indicator */}
  <div className="flex items-center justify-center space-x-2 mb-6">
    {[1, 2, 3].map((step) => (
      <React.Fragment key={step}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
          forgotStep >= step
            ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white'
            : 'bg-gray-200 text-gray-400'
        }`}>
          {step}
        </div>
        {step < 3 && (
          <div className={`h-1 w-10 rounded transition-all ${
            forgotStep > step ? 'bg-gradient-to-r from-blue-600 to-cyan-500' : 'bg-gray-200'
          }`} />
        )}
      </React.Fragment>
    ))}
  </div>

  <h2 className="text-2xl sm:text-3xl text-gray-800 mb-2">
    {forgotStep === 1 && 'Reset Password'}
    {forgotStep === 2 && 'Verify Code'}
    {forgotStep === 3 && 'New Password'}
  </h2>
  <p className="text-gray-600 mb-6 text-sm sm:text-base">
    {forgotStep === 1 && 'Enter your registered email address'}
    {forgotStep === 2 && `Enter the 6-digit code sent to ${forgotData.email}`}
    {forgotStep === 3 && 'Create your new secure password'}
  </p>

  <div className="space-y-4">

    {/* Step 1: Email */}
    {forgotStep === 1 && (
      <div>
        <label className="block text-sm sm:text-base text-gray-700 mb-2">Email Address</label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
          <input
            type="email"
            placeholder="your.email@vdartinc.com"
            value={forgotData.email}
            onChange={(e) => setForgotData({ ...forgotData, email: e.target.value })}
            onKeyPress={(e) => e.key === 'Enter' && handleForgotPassword()}
            className={`w-full pl-10 sm:pl-11 pr-4 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
              formErrors.email ? 'border-red-400' : 'border-gray-300'
            }`}
            autoFocus
          />
        </div>
        {formErrors.email && (
          <p className="text-red-500 text-sm mt-2 flex items-center">
            <AlertTriangle className="w-4 h-4 mr-1" />
            {formErrors.email}
          </p>
        )}
      </div>
    )}

    {/* Step 2: OTP */}
    {forgotStep === 2 && (
      <div>
        <label className="block text-sm sm:text-base text-gray-700 mb-2 text-center">Verification Code</label>
        <input
          type="text"
          maxLength={6}
          placeholder="000000"
          value={forgotData.otp}
          onChange={(e) => setForgotData({ ...forgotData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
          onKeyPress={(e) => e.key === 'Enter' && handleForgotPassword()}
          className={`w-full px-4 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl sm:text-3xl tracking-widest ${
            formErrors.otp ? 'border-red-400' : 'border-gray-300'
          }`}
          autoFocus
        />
        {formErrors.otp && (
          <p className="text-red-500 text-sm mt-2 text-center flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 mr-1" />
            {formErrors.otp}
          </p>
        )}
        <button
          onClick={async () => {
            setIsLoading(true);
            try {
              const response = await fetch('https://test.vdpl.co/Candidates/Candidates/forgot_password.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotData.email })
              });
              const data = await response.json();
              setToast({ show: true, message: data.success ? 'New code sent!' : (data.message || 'Failed to resend'), type: data.success ? 'success' : 'error' });
            } catch {
              setToast({ show: true, message: 'Failed to resend code', type: 'error' });
            } finally {
              setIsLoading(false);
            }
          }}
          disabled={isLoading}
          className="w-full text-blue-600 hover:underline text-sm sm:text-base flex items-center justify-center mt-2"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Resend Code
        </button>
      </div>
    )}

    {/* Step 3: New Password */}
    {forgotStep === 3 && (
      <>
        <div>
          <label className="block text-sm sm:text-base text-gray-700 mb-2">New Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type={showNewPassword ? "text" : "password"}
              placeholder="Min 12 chars, upper, lower, number, symbol"
              value={forgotData.newPassword}
              onChange={(e) => setForgotData({ ...forgotData, newPassword: e.target.value })}
              className={`w-full pl-10 sm:pl-11 pr-12 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                formErrors.newPassword ? 'border-red-400' : 'border-gray-300'
              }`}
              autoFocus
            />
            <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {showNewPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
            </button>
          </div>
          {formErrors.newPassword && <p className="text-red-500 text-sm mt-2">{formErrors.newPassword}</p>}
        </div>

        <div>
          <label className="block text-sm sm:text-base text-gray-700 mb-2">Confirm New Password</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type={showConfirmNewPassword ? "text" : "password"}
              placeholder="Confirm new password"
              value={forgotData.confirmPassword}
              onChange={(e) => setForgotData({ ...forgotData, confirmPassword: e.target.value })}
              onKeyPress={(e) => e.key === 'Enter' && handleForgotPassword()}
              className={`w-full pl-10 sm:pl-11 pr-12 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                formErrors.confirmPassword ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            <button type="button" onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {showConfirmNewPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
            </button>
          </div>
          {formErrors.confirmPassword && <p className="text-red-500 text-sm mt-2">{formErrors.confirmPassword}</p>}
        </div>
      </>
    )}

    <button
      onClick={handleForgotPassword}
      disabled={isLoading}
      className={`w-full py-3 sm:py-3.5 rounded-lg transition-all duration-300 text-sm sm:text-base ${
        isLoading
          ? 'bg-gray-400 cursor-not-allowed'
          : 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white hover:shadow-lg transform hover:scale-105'
      }`}
    >
      {isLoading ? (
        <div className="flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
          Processing...
        </div>
      ) : (
        <>
          {forgotStep === 1 && 'Send Verification Code'}
          {forgotStep === 2 && 'Verify Code'}
          {forgotStep === 3 && 'Reset Password'}
        </>
      )}
    </button>
  </div>

  <div className="mt-6 text-center">
    <button
      onClick={() => {
        setIsForgotPassword(false);
        setForgotStep(1);
        setForgotData({ email: '', otp: '', newPassword: '', confirmPassword: '' });
        setFormErrors({});
      }}
      className="text-blue-600 hover:underline text-sm sm:text-base flex items-center justify-center mx-auto"
    >
      <ArrowRight className="w-4 h-4 mr-1 rotate-180" />
      Back to Sign In
    </button>
  </div>
</div>

            {/* Sign Up Form */}
            <div className={`transition-all duration-500 ${isSignUp ? 'block' : 'hidden'}`}>
              {!otpData.isOtpSent ? (
                <>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl text-gray-800 mb-2">Create Account</h2>
                  <p className="text-gray-600 mb-6 lg:mb-7 text-sm sm:text-base">Join our candidate management platform</p>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm sm:text-base text-gray-700 mb-2">First Name</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                          <input
                            type="text"
                            placeholder="John"
                            value={signupData.first_name}
                            onChange={(e) => setSignupData({ ...signupData, first_name: e.target.value })}
                            onKeyPress={handleSignupKeyPress}
                            className={`w-full pl-10 sm:pl-11 pr-4 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                              formErrors.first_name ? 'border-red-400' : 'border-gray-300'
                            }`}
                          />
                        </div>
                        {formErrors.first_name && <p className="text-red-500 text-sm mt-2">{formErrors.first_name}</p>}
                      </div>
                      
                      <div>
                        <label className="block text-sm sm:text-base text-gray-700 mb-2">Last Name</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Doe"
                            value={signupData.last_name}
                            onChange={(e) => setSignupData({ ...signupData, last_name: e.target.value })}
                            onKeyPress={handleSignupKeyPress}
                            className={`w-full pl-10 sm:pl-11 pr-4 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                              formErrors.last_name ? 'border-red-400' : 'border-gray-300'
                            }`}
                          />
                        </div>
                        {formErrors.last_name && <p className="text-red-500 text-sm mt-2">{formErrors.last_name}</p>}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm sm:text-base text-gray-700 mb-2">Email Address</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        <input
                          type="email"
                          placeholder="john.doe@vdartinc.com"
                          value={signupData.email}
                          onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                          onKeyPress={handleSignupKeyPress}
                          className={`w-full pl-10 sm:pl-11 pr-4 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                            formErrors.email ? 'border-red-400' : 'border-gray-300'
                          }`}
                        />
                      </div>
                      {formErrors.email && <p className="text-red-500 text-sm mt-2">{formErrors.email}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm sm:text-base text-gray-700 mb-2">Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        <input
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={signupData.password}
                          onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                          onKeyPress={handleSignupKeyPress}
                          className={`w-full pl-10 sm:pl-11 pr-12 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                            formErrors.password ? 'border-red-400' : 'border-gray-300'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
                        </button>
                      </div>
                      {formErrors.password && <p className="text-red-500 text-sm mt-2">{formErrors.password}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-sm sm:text-base text-gray-700 mb-2">Confirm Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm password"
                          value={signupData.confirm_password}
                          onChange={(e) => setSignupData({ ...signupData, confirm_password: e.target.value })}
                          onKeyPress={handleSignupKeyPress}
                          className={`w-full pl-10 sm:pl-11 pr-12 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base ${
                            formErrors.confirm_password ? 'border-red-400' : 'border-gray-300'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />}
                        </button>
                      </div>
                      {formErrors.confirm_password && <p className="text-red-500 text-sm mt-2">{formErrors.confirm_password}</p>}
                    </div>
                    
                    <button
                      onClick={handleSignup}
                      disabled={isLoading}
                      className={`w-full py-3 sm:py-3.5 rounded-lg transition-all duration-300 text-sm sm:text-base ${
                        isLoading 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white hover:shadow-lg transform hover:scale-105'
                      }`}
                    >
                      {isLoading ? 'Creating Account...' : 'Create Account'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl text-gray-800 mb-2">Verify Email</h2>
                  <p className="text-gray-600 mb-6 text-sm sm:text-base">Enter the 6-digit code sent to {otpData.email}</p>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm sm:text-base text-gray-700 mb-2 text-center">Verification Code</label>
                      <input
                        type="text"
                        maxLength={6}
                        placeholder="000000"
                        value={otpData.otp}
                        onChange={(e) => setOtpData({ ...otpData, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                        onKeyPress={handleSignupKeyPress}
                        className={`w-full px-4 py-3 sm:py-3.5 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl sm:text-3xl tracking-widest ${
                          formErrors.otp ? 'border-red-400' : 'border-gray-300'
                        }`}
                      />
                      {formErrors.otp && <p className="text-red-500 text-sm mt-2 text-center">{formErrors.otp}</p>}
                    </div>
                    
                    <button
                      onClick={handleSignup}
                      disabled={isLoading}
                      className={`w-full py-3 sm:py-3.5 rounded-lg transition-all duration-300 text-sm sm:text-base ${
                        isLoading 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white hover:shadow-lg transform hover:scale-105'
                      }`}
                    >
                      {isLoading ? 'Verifying...' : 'Verify & Create Account'}
                    </button>
                    
                    <button
                      onClick={handleResendOtp}
                      disabled={isLoading}
                      className="w-full text-blue-600 hover:underline text-sm sm:text-base flex items-center justify-center"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Code
                    </button>
                  </div>
                </>
              )}
              
              <div className="mt-6 text-center">
                <p className="text-gray-600 text-sm sm:text-base">
                  Already have an account?{' '}
                  <button
                    onClick={() => {
                      setIsSignUp(false);
                      setFormErrors({});
                      setOtpData({ email: '', otp: '', isOtpSent: false, isVerifying: false });
                    }}
                    className="text-blue-600 hover:underline"
                  >
                    Sign In
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}